import styled, { css } from 'styled-components'
import { cardisTheme } from '../../theme/cardisTheme'

// Re-export shared components for backward compatibility
export {
  PageContainer,
  ContentArea,
  SectionHeader,
  FooterWrapper,
  FooterPattern,
  Footer,
  PageNumber,
} from '../CardisPropertyCharacteristics/CardisPropertyCharacteristics.styles'

const printStyles = css`
  @media print {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const Title = styled.h1`
  font-family: ${cardisTheme.fonts.body};
  font-size: 13px;
  font-weight: 500;
  color: ${cardisTheme.colors.secondary};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media print {
    ${printStyles}
  }
`

export const IntroText = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textDark};
  margin: 0 0 16px 0;
  line-height: 1.6;

  strong {
    font-weight: 600;
  }

  @media print {
    font-size: 10px !important;
    color: ${cardisTheme.colors.textDark} !important;
    margin: 0 0 16px 0 !important;
    line-height: 1.6 !important;
    ${printStyles}
  }
`

export const PriceRangeBanner = styled.div`
  background-color: ${cardisTheme.colors.primary};
  padding: 13px 26px;
  margin-left: -${cardisTheme.spacing.pageMargin};
  margin-right: -${cardisTheme.spacing.pageMargin};
  margin-bottom: 20px;
  text-align: center;

  @media print {
    ${printStyles}
  }
`

export const PriceRangeText = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 26px;
  font-weight: 600;
  color: ${cardisTheme.colors.white};
  letter-spacing: 0.5px;

  @media print {
    ${printStyles}
  }
`

export const PriceRangeTextItalic = styled.span`
  font-style: italic;
  font-size: 15px;
  font-weight: 400;

  @media print {
    ${printStyles}
  }
`

export const BrokerCommentSection = styled.div`
  margin-bottom: 20px;
`

export const CommentHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 13px 0;
`

export const ToggleIconButton = styled.button<{ $isHidden?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  opacity: ${({ $isHidden }) => ($isHidden ? 0.4 : 0.6)};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  @media print {
    display: none !important;
  }
`

export const SectionTitle = styled.h2`
  font-family: ${cardisTheme.fonts.body};
  font-size: 15px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  margin: 0 0 13px 0;

  @media print {
    font-size: 15px !important;
    font-weight: 600 !important;
    color: ${cardisTheme.colors.primary} !important;
    margin: 0 0 13px 0 !important;
    ${printStyles}
  }
`

export const CommentBox = styled.div`
  display: flex;
  gap: 26px;
  align-items: stretch;

  @media print {
    display: flex !important;
    gap: 26px !important;
  }
`

export const LeftColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

export const CommentText = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 400;
  color: ${cardisTheme.colors.textDark};
  margin: 0 0 20px 0;
  line-height: 1.3;
  text-align: justify;

  strong {
    font-weight: 700;
  }

  @media print {
    ${printStyles}
  }
`

export const BrokerInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: auto;
`

export const BrokerPhoto = styled.img`
  width: 112px;
  height: 112px;
  border-radius: 50%;
  object-fit: cover;

  @media print {
    width: 112px !important;
    height: 112px !important;
    border-radius: 50% !important;
    ${printStyles}
  }
`

export const BrokerDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

export const BrokerName = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 15px;
  font-weight: 700;
  color: ${cardisTheme.colors.primary};

  @media print {
    ${printStyles}
  }
`

export const BrokerTitle = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textGray};

  @media print {
    ${printStyles}
  }
`

export const BrokerEmail = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textDark};

  @media print {
    ${printStyles}
  }
`

export const BrokerPhone = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 700;
  color: ${cardisTheme.colors.textDark};

  @media print {
    ${printStyles}
  }
`

export const StatisticsBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f5f3ef;
  border-radius: 6px;
  padding: 20px;
  margin-top: 50px;
  text-align: center;

  @media print {
    ${printStyles}
  }
`

export const StatisticsHeader = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 13px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0 0 20px 0;
  line-height: 1.3;

  @media print {
    ${printStyles}
  }
`

export const StatisticsHighlight = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 15px;
  font-weight: 700;
  color: ${cardisTheme.colors.primary};
  margin: 0;
  line-height: 1.3;

  @media print {
    ${printStyles}
  }
`

// Legacy - keeping for backwards compatibility
export const RightColumn = StatisticsBox

export const StatisticsText = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textDark};
  margin: 0;
  line-height: 1.6;

  strong {
    font-weight: 700;
    color: ${cardisTheme.colors.primary};
  }

  @media print {
    font-size: 10px !important;
    color: ${cardisTheme.colors.textDark} !important;
    line-height: 1.6 !important;
    ${printStyles}
  }
`

export const TeamSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`

export const TeamTitle = styled.h2`
  font-family: ${cardisTheme.fonts.body};
  font-size: 13px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 0 0 13px 0;
  letter-spacing: 0.5px;

  @media print {
    ${printStyles}
  }
`

export const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 13px;
  padding-top: 6px;

  @media print {
  }
`

export const TeamMemberCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`

export const TeamMemberPhoto = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 5px;

  @media print {
    width: 56px !important;
    height: 56px !important;
    border-radius: 50% !important;
    margin-bottom: 5px !important;
    ${printStyles}
  }
`

export const TeamMemberName = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 7px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};
  line-height: 1.2;
  text-align: left;
  width: 100%;

  @media print {
    ${printStyles}
  }
`

export const TeamMemberTitle = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 6px;
  font-weight: 400;
  color: ${cardisTheme.colors.textGray};
  line-height: 1.2;
  text-align: left;
  width: 100%;
  padding-top: 3px;

  @media print {
    ${printStyles}
  }
`

export const TeamLogoRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

export const CardisLogo = styled.img`
  width: 96px;
  height: auto;
  object-fit: contain;

  @media print {
    width: 96px !important;
    height: auto !important;
    ${printStyles}
  }
`
